import { Directory, Encoding, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { Button } from '@up2stay/up2promo-ui';
import { cn } from '@up2stay/up2promo-ui';
import { Download, Upload } from 'lucide-react';
import { useRef } from 'react';
import readXlsxFile from 'read-excel-file/browser';
import { toast } from 'sonner';

import { escapeCsvCell } from '@/core/lib/csv-cell';
import type { TemplateMemberValueColumn } from '@/core/lib/profile-template-member-values';
import type { MemberRosterRow } from '@/core/lib/profile-template-member-values-csv';
import {
  exportMemberValuesCsvFromValues,
  importMemberValuesCsvToUpdates,
} from '@/core/lib/profile-template-member-values-csv';
import { isCapacitorNative } from '@/core/utils/capacitor.utils';

const XLSX_CSV_DELIMITER = ';';

function xlsxCellToString(value: unknown): string {
  if (value == null) {
    return '';
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  return String(value);
}

async function worksheetToSemicolonCsv(buffer: ArrayBuffer): Promise<string> {
  const sheets = await readXlsxFile(buffer);
  const first = sheets[0];
  if (!first) {
    throw new Error('missingSheet');
  }

  return first.data
    .map((row) =>
      row
        .map((cell) =>
          escapeCsvCell(xlsxCellToString(cell), XLSX_CSV_DELIMITER),
        )
        .join(XLSX_CSV_DELIMITER),
    )
    .join('\n');
}

function columnCsvFilename(column: TemplateMemberValueColumn): string {
  const slug =
    column.kind === 'field'
      ? column.key
      : column.kind === 'social'
        ? column.platform
        : column.kind === 'moduleEntry'
          ? column.entryId
          : column.key;
  return `member-values-${slug}.csv`;
}

export interface TemplateMemberValuesCsvActionsProps {
  column: TemplateMemberValueColumn;
  roster: MemberRosterRow[];
  columnValues: Record<string, string>;
  defaultValue: string;
  onBulkUpdate: (updates: Record<string, string>) => void;
  canManage: boolean;
  disabled?: boolean;
  stacked?: boolean;
  className?: string;
  dict: {
    exportCsv: string;
    importCsv: string;
    importSuccess: string;
    importError: string;
  };
}

export function TemplateMemberValuesCsvActions({
  column,
  roster,
  columnValues,
  defaultValue,
  onBulkUpdate,
  canManage,
  disabled = false,
  stacked = false,
  className,
  dict,
}: TemplateMemberValuesCsvActionsProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!canManage || roster.length === 0) {
    return null;
  }

  const handleExport = async () => {
    const csv = exportMemberValuesCsvFromValues({
      roster,
      values: columnValues,
      columns: [column],
      getCellValue: (entry) => (typeof entry === 'string' ? entry : ''),
    });
    const filename = columnCsvFilename(column);

    if (isCapacitorNative()) {
      try {
        const writeResult = await Filesystem.writeFile({
          path: filename,
          data: csv,
          directory: Directory.Cache,
          encoding: Encoding.UTF8,
        });
        await Share.share({
          title: filename,
          text: filename,
          url: writeResult.uri,
        });
        return;
      } catch (error) {
        console.error(
          'Native CSV export failed, using browser fallback',
          error,
        );
      }
    }

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportFile = async (file: File) => {
    try {
      const name = file.name.toLowerCase();
      let text = '';

      if (name.endsWith('.xlsx')) {
        const buffer = await file.arrayBuffer();
        text = await worksheetToSemicolonCsv(buffer);
      } else {
        text = await file.text();
      }

      const updates = importMemberValuesCsvToUpdates({
        csvText: text,
        roster,
        columns: [column],
        existing: columnValues,
        defaultValue,
      });
      if (Object.keys(updates).length === 0) {
        toast.success(dict.importSuccess);
        return;
      }
      onBulkUpdate(updates);
      toast.success(dict.importSuccess);
    } catch (err) {
      console.error(err);
      toast.error(dict.importError);
    }
  };

  return (
    <div
      className={cn(
        'flex gap-2',
        stacked ? 'flex-col [&_button]:w-full' : 'flex-wrap',
        className,
      )}
    >
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-2 h-8"
        disabled={disabled}
        onClick={() => {
          void handleExport();
        }}
      >
        <Download className="h-3.5 w-3.5 shrink-0" />
        {dict.exportCsv}
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-2 h-8"
        disabled={disabled}
        onClick={() => fileInputRef.current?.click()}
      >
        <Upload className="h-3.5 w-3.5 shrink-0" />
        {dict.importCsv}
      </Button>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) void handleImportFile(file);
        }}
      />
    </div>
  );
}
